import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Box,
    Flex,
    Text,
    Button,
    VStack,
    Heading,
    HStack,
    Skeleton,
    Badge,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'

// project components
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import DisplayPrice from '../../display-price'
import PromoCallout from '../../promo-callout'
import AfterPayMessage from './bnpl-messages/afterpay-message'
import {useGlobaleContext} from '../../../contexts/globale'
import {useSiteContext} from '../../../contexts/site'
import breakpoints from '../../../theme/foundations/breakpoints'
import ProductRating, {shouldShowPromotedProductRating} from './product-rating'
import useContentAssetConfig from '../../../hooks/use-content-asset-config'
import useDeferredNavigation from '../../../hooks/use-deferred-navigation'
import {getBadgeUrl, isCharmBadge, selectPdpHeaderBadges} from '../../../utils/product-utils'

export const ProductViewHeader = ({
    name,
    currency,
    priceData,
    priceDataSets,
    globalePrices,
    pwpTargetPromotionId,
    product,
    variant,
    isProductPartOfBundle,
    wishlist,
    showPriceSkeleton,
    isEngagementProduct = false,
    color,
    selectedCarat,
    BNPLMessagesRef,
    isCart
}) => {
    const {isYR} = useSiteContext()
    const styles = useStyleConfig('ProductView', {variant: isYR ? 'YR' : 'KS'})
    const badgeUrlConfig = useContentAssetConfig(isCart ? null : 'badge-url-config')
    const deferredNavigate = useDeferredNavigation()
    const imageBadges = selectPdpHeaderBadges(product)
    const hasCharmBadge = imageBadges?.some(isCharmBadge)

    const scrollToStickyChild = (ref) => {
        if (!ref?.current) return

        const target = ref.current
        const sticky = target.closest('.pdp-product-details')
        const isMobile = window.matchMedia(`(max-width: ${breakpoints.lg})`).matches
        const stickyTop = sticky ? parseInt(getComputedStyle(sticky).top, 15) || 0 : 0

        const {top} = target.getBoundingClientRect()

        const gallery = document.querySelector('.pdp-image-gallery')
        const productDetails = document.querySelector('.pdp-product-details')

        if (!gallery || !productDetails) return

        const containerHeight = gallery.getBoundingClientRect().height
        const contentHeight = productDetails.scrollHeight

        const totalHeight =
            containerHeight > contentHeight + 400 ? containerHeight - contentHeight : 0
        const y = window.scrollY + top - (isMobile ? 100 : stickyTop) + totalHeight

        window.scrollTo({
            top: y,
            behavior: 'smooth'
        })
    }
    const {
        app: {commerceAPI}
    } = getConfig()

    const {affirm, paypal, afterpay} = commerceAPI
    const {configAffirm} = affirm || {}
    const {configPaypal} = paypal || {}
    const {configAfterpay} = afterpay || {}

    //paypal
    var paypalMinTotal = 50.0
    var enablePayPalPDPCallout = false

    //affirm
    var affirmMinTotal = 50.0
    var enableAffirmPDPCallout = false

    //afterpay
    var enableAfterpayPDPCallout = false
    var afterpayScriptUrl = null
    var apPDPPlacementId = null
    var apPDPMpid = null

    try {
        const affirmJsonConfig = JSON.parse(configAffirm)
        const afterpayJsonConfig = JSON.parse(configAfterpay)
        const paypalJsonConfig = JSON.parse(configPaypal)

        paypalMinTotal = paypalJsonConfig.minTotal
        enablePayPalPDPCallout = paypalJsonConfig.enablePDPCallout

        //affirm
        affirmMinTotal = affirmJsonConfig.minTotal
        enableAffirmPDPCallout = affirmJsonConfig.enablePDPCallout

        //afterpay
        enableAfterpayPDPCallout = afterpayJsonConfig.enablePDPCallout
        afterpayScriptUrl = afterpayJsonConfig.scriptURL
        apPDPPlacementId = afterpayJsonConfig.pdpPlacementID
        apPDPMpid = afterpayJsonConfig.pdpMPID
    } catch (e) {
        console.log(e.message)
    }

    const intl = useIntl()
    const isThreeTierdPricing = !!(
        product?.tieredPrices?.[0]?.price &&
        product?.tieredPrices?.[1]?.price &&
        product?.tieredPrices?.[0]?.price != product?.tieredPrices?.[1]?.price &&
        product?.productPromotions?.some((promo) => promo?.promotionalPrice)
    )
    if (isThreeTierdPricing) {
        priceData.listPrice = product?.tieredPrices?.[1]?.price
    }

    const {countryISO} = useGlobaleContext()

    const price = priceData?.currentPrice || variant?.price
    const paypalMessageEligible = price > paypalMinTotal && enablePayPalPDPCallout
    const affirmMessageEligible = price > affirmMinTotal && enableAffirmPDPCallout
    const isAfterPayEnabled =
        enableAfterpayPDPCallout && apPDPMpid && apPDPPlacementId && afterpayScriptUrl
    const [salePriceColor, setSalePriceColor] = useState('green.500')

    if (product && globalePrices && pwpTargetPromotionId) {
        const promoObj = globalePrices?.find((p) => p?.[pwpTargetPromotionId])?.[
            pwpTargetPromotionId
        ]
        priceData.currentPrice =
            promoObj?.[product?.master?.masterId] !== null
                ? promoObj?.[product?.master?.masterId]
                : product?.price || 0
        priceData.isOnSale = priceData.listPrice !== priceData.currentPrice
    }

    return (
        <VStack key="product-view-header" mr={0} spacing="0" align="flex-start" {...styles.price}>
            {/* Title */}
            <Skeleton
                key="product-name-wishlist-wrapper"
                isLoaded={name}
                width="full"
                display={product?.type?.set && product?.c_hideNameAndPrice ? 'none' : 'ruby'}
                marginBottom={{
                    base: '5px',
                    lg: product?.type?.set ? '10px' : '15px'
                }}
            >
                <Box
                    display={imageBadges?.length && !isCart ? 'inline-flex' : 'none'}
                    className={`badge-box ${hasCharmBadge ? 'charm-badge' : ''}`}
                    sx={{
                        '&.charm-badge p': {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '7px',
                            textWrap: 'auto'
                        },
                        '&.charm-badge img': {
                            display: 'flex',
                            width: '12px',
                            margin: '0px'
                        }
                    }}
                >
                    {imageBadges?.map((badge, index) => {
                        const badgeUrl = getBadgeUrl(badge, badgeUrlConfig)

                        return (
                            <Badge
                                key={index}
                                {...(badgeUrl ? {as: Link, to: badgeUrl} : {})}
                                onClick={
                                    badgeUrl
                                        ? (event) => {
                                              event.stopPropagation()
                                              deferredNavigate(event, badgeUrl)
                                          }
                                        : undefined
                                }
                                display={{
                                    base: index ? 'none' : 'block',
                                    lg: index > 1 ? 'none' : 'block'
                                }}
                                variant="imageBadge"
                                _hover={{textDecoration: 'none'}}
                                sx={{
                                    div: {
                                        alignItems: 'center',
                                        display: 'inline-flex',
                                        height: '24px',
                                        letterSpacing: '0.8px'
                                    },
                                    '.show-badge': {
                                        display: 'block !important'
                                    },
                                    img: {
                                        width: '15px',
                                        height: '14px',
                                        marginRight: '8px'
                                    },
                                    'img:not(.charms)': {
                                        display: 'none'
                                    },
                                    p: {
                                        lineHeight: '22px'
                                    }
                                }}
                                dangerouslySetInnerHTML={{__html: badge}}
                            />
                        )
                    })}
                </Box>
                <HStack key="product-name-wishlist" width="full" gap="20px">
                    <Heading
                        as="h1"
                        className="pdp-product-name"
                        key="product-name"
                        {...(product?.type?.set ? styles.setTitle : styles.title)}
                    >{`${name}`}</Heading>

                    {
                        <Box
                            key="wishlist-box"
                            width={product?.type?.set ? '30px' : {base: '40px', lg: '48px'}}
                            height={product?.type?.set ? '30px' : {base: '40px', lg: '48px'}}
                            className="wishlist-box"
                            marginLeft="auto"
                            alignContent="center"
                        >
                            {React.Children.map(wishlist(), (child, index) =>
                                React.cloneElement(child, {key: `wishlist-item-${index}`})
                            )}
                        </Box>
                    }
                </HStack>
                {shouldShowPromotedProductRating(product, {isCart, isEngagementProduct}) && (
                    <Box
                        id="pdp-title-rating-container"
                        data-monetate-id="pdp-title-rating-container"
                        height="31px"
                        display="flex"
                    >
                        <ProductRating product={product} placement="title" />
                    </Box>
                )}
            </Skeleton>

            {!isProductPartOfBundle && (
                <Flex
                    flexWrap="wrap"
                    gap={isEngagementProduct ? '0' : '10px'}
                    width="full"
                    display={product?.type?.set && product?.c_hideNameAndPrice ? 'none' : 'flex'}
                >
                    <Box
                        width="full"
                        display={
                            isThreeTierdPricing || isEngagementProduct ? 'inline-flex' : 'none'
                        }
                        marginBottom="-5px"
                        sx={{
                            '& p': {
                                color: 'gray.800',
                                fontSize: '13px',
                                fontWeight: '300',
                                letterSpacing: '0.065px',
                                lineHeight: '22px'
                            }
                        }}
                    >
                        {isEngagementProduct &&
                            !selectedCarat &&
                            product?.type?.master &&
                            !showPriceSkeleton && (
                                <Text
                                    display={selectedCarat ? 'none' : 'flex'}
                                    {...styles.startingAt}
                                >
                                    <FormattedMessage
                                        id="starting.at"
                                        defaultMessage="starting at"
                                    />
                                </Text>
                            )}
                        {isThreeTierdPricing && (
                            <Box
                                width="full"
                                display="inline-flex"
                                marginBottom="-5px"
                                sx={{
                                    '& p': {
                                        color: 'gray.800',
                                        fontSize: '13px',
                                        fontWeight: '300',
                                        letterSpacing: '0.065px',
                                        lineHeight: '22px'
                                    }
                                }}
                            >
                                <Text className="original-text">
                                    {!showPriceSkeleton ? (
                                        <FormattedMessage id="original" defaultMessage="Original" />
                                    ) : (
                                        <Skeleton marginTop="8px" height="16px" width="49px" />
                                    )}
                                    &nbsp;
                                </Text>
                                {product?.c_selectedCurrency &&
                                product?.c_selectedCurrencySymbol &&
                                !showPriceSkeleton ? (
                                    <Text textDecoration="line-through" className="tiered-price">
                                        {intl
                                            .formatNumber(product?.tieredPrices?.[0]?.price, {
                                                style: 'currency',
                                                currency: product?.c_selectedCurrency
                                            })
                                            ?.replace(
                                                product?.c_selectedCurrency,
                                                product?.c_selectedCurrencySymbol
                                            )}
                                        {/** Make the currency dynamic once we implement Globale prices. **/}
                                    </Text>
                                ) : (
                                    <Skeleton height="16px" width="59px" marginTop="8px" />
                                )}
                            </Box>
                        )}
                    </Box>
                    <Skeleton
                        key="product-price"
                        isLoaded={
                            priceData?.currentPrice && countryISO !== null && !showPriceSkeleton
                        }
                        sx={{
                            '.list-price': {
                                fontSize: '14px'
                            },
                            '.current-price': {
                                fontSize: '14px'
                            }
                        }}
                    >
                        {priceData.isASet
                            ? priceDataSets?.currentPrice && (
                                  <DisplayPrice
                                      priceData={priceDataSets}
                                      currency={product?.c_selectedCurrency || currency}
                                      symbol={product?.c_selectedCurrencySymbol}
                                      listPriceProps={{marginRight: '10px'}}
                                      salePriceColor={salePriceColor}
                                      currentPriceProps={{
                                          fontWeight: '400'
                                      }}
                                  />
                              )
                            : priceData?.currentPrice && (
                                  <DisplayPrice
                                      priceData={priceData}
                                      currency={product?.c_selectedCurrency || currency}
                                      symbol={product?.c_selectedCurrencySymbol}
                                      listPriceProps={{marginRight: '10px'}}
                                      salePriceColor={salePriceColor}
                                      currentPriceProps={{
                                          fontWeight: '400'
                                      }}
                                  />
                              )}
                    </Skeleton>

                    {!isCart &&
                        (product?.productPromotions || product?.c_promotions) &&
                        !isEngagementProduct && (
                            <PromoCallout
                                key="product-promotion"
                                product={product}
                                priceData={priceData}
                                setColor={(value) => {
                                    setSalePriceColor(value)
                                }}
                                promotions={product?.c_promotions}
                                productPromotions={product?.productPromotions}
                                color={color}
                            />
                        )}
                </Flex>
            )}
            {!isCart &&
                countryISO === 'US' &&
                ((variant?.price && variant?.productId) ||
                    (product?.type?.set && product?.price)) &&
                isAfterPayEnabled &&
                !isEngagementProduct && (
                    <Box
                        ref={BNPLMessagesRef}
                        paddingTop={
                            product?.type?.set && product?.c_hideNameAndPrice
                                ? '0px'
                                : {base: '10px', lg: '20px'}
                        }
                        paddingBottom="5px"
                        backgroundColor="transparent"
                        borderStyle="solid"
                        borderBottom="1px"
                        borderColor="gray.400"
                        maxWidth="340px"
                        display={product?.type?.set && product?.c_hideBNPL ? 'none' : 'block'}
                    >
                        {isAfterPayEnabled && (
                            <AfterPayMessage
                                price={price}
                                variant={product?.type?.set ? product : variant}
                            />
                        )}

                        {isAfterPayEnabled && paypalMessageEligible && affirmMessageEligible && (
                            <Button
                                onClick={() => scrollToStickyChild(BNPLMessagesRef)}
                                variant="unstyled"
                                fontSize="12px"
                                textDecoration="underline"
                                textUnderlineOffset="4px"
                                cursor="pointer"
                                color="black.100"
                                lineHeight="23px"
                                fontWeight="300"
                                height="auto"
                                minHeight="none"
                                padding="0"
                                _hover={{
                                    color: 'teal.600'
                                }}
                            >
                                {intl.formatMessage({
                                    defaultMessage: 'More Financing Options',
                                    id: 'more.financing.options'
                                })}
                            </Button>
                        )}
                    </Box>
                )}
        </VStack>
    )
}

ProductViewHeader.propTypes = {
    name: PropTypes.string,
    currency: PropTypes.string,
    priceData: PropTypes.object,
    priceDataSets: PropTypes.object,
    globalePrices: PropTypes.array,
    pwpTargetPromotionId: PropTypes.string,
    product: PropTypes.object,
    promotions: PropTypes.any,
    color: PropTypes.any,
    showPriceSkeleton: PropTypes.bool,
    isEngagementProduct: PropTypes.bool,
    variant: PropTypes.any,
    isProductPartOfBundle: PropTypes.bool,
    wishlist: PropTypes.func,
    productDetailsRef: PropTypes.object,
    BNPLMessagesRef: PropTypes.object,
    galleryRef: PropTypes.object,
    selectedCarat: PropTypes.bool,
    isCart: PropTypes.any
}
